/**
 * Billing configuration — single source of truth for pricing.
 *
 * To change the per-minute rate:
 *   1. Update USAGE_RATE_PER_MINUTE below.
 *   2. Redeploy edge functions: `supabase functions deploy`.
 *   3. Create a new SQL migration that re-creates the four functions carrying
 *      the default literal (latest copy, with the list:
 *      supabase/migrations/20261010120000_default_usage_rate_015.sql). Their
 *      rate MUST match the value below — otherwise `deduct_workspace_usage`
 *      (which actually charges money) uses a different rate than the gates and UI.
 *   4. Run `supabase db push` to apply the migration to your database.
 *
 * NOTE: React (src/lib/billing-config.ts), the Angular dashboard
 * (src/app/core/billing-config.ts) and the admin portal (DEFAULT_USAGE_RATE)
 * re-export this constant; none of them keeps its own copy.
 */

/** Cost per minute of voice call usage, in USD (= "credits", 1 credit = $1). */
export const USAGE_RATE_PER_MINUTE = 0.15;

/**
 * Minimal shape required to resolve a workspace's effective per-minute rate.
 * Any code that loads workspaces via `.select(... usage_rate_per_minute_override ...)`
 * can pass the row through `getWorkspaceUsageRate()` directly.
 */
export interface WorkspaceRateRow {
  usage_rate_per_minute_override?: number | null;
}

/**
 * Effective per-minute rate for a workspace.
 *
 * Returns the per-workspace override when set, otherwise the global default
 * (`USAGE_RATE_PER_MINUTE`). Mirrors the SQL `COALESCE(override, 0.15)` used
 * inside `deduct_workspace_usage` so the TS gates/webhooks and the SQL that
 * actually charges money always agree on the rate.
 */
export const getWorkspaceUsageRate = (
  w: WorkspaceRateRow | null | undefined,
): number => w?.usage_rate_per_minute_override ?? USAGE_RATE_PER_MINUTE;

/**
 * The knowledge-base line, USD per minute, on top of the workspace's rate (default or override) for a call that
 * ran automatic knowledge-base search (owner decisions, 2026-10-13). Free minutes cover it. MUST equal the 0.01 in
 * `deduct_workspace_usage` (supabase/migrations/20261013140000_kb_price_line.sql), which is what charges it.
 */
export const KB_RATE_PER_MINUTE = 0.01;

/** The column a call row says it ran automatic knowledge-base search with (stamped by the bot). */
export interface CallRateRow {
  kb_auto_search?: boolean | null;
}

/** One call's per-minute rate, from its workspace's: plus the knowledge-base line when it ran automatic search. */
export const callUsageRate = (
  workspaceRate: number,
  call: CallRateRow | null | undefined,
): number => workspaceRate + (call?.kb_auto_search ? KB_RATE_PER_MINUTE : 0);
